import * as Dialog from '@radix-ui/react-dialog';
import classNames from 'classnames';

import { MobileMenuAccordion } from './MobileMenuAccordion';
import styles from './MobileNavigation.module.scss';

import type { HeaderMenuItem } from '../../../types';

type MobileNavigationProps = {
  id: string;
  isOpen: boolean;
  items: HeaderMenuItem[];
  onClose: () => void;
  hasAnnouncement: boolean;
  container?: HTMLElement | null;
};

export function MobileNavigation({
  id,
  isOpen,
  items,
  onClose,
  hasAnnouncement,
  container,
}: MobileNavigationProps) {
  if (items.length === 0) {
    return null;
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      return;
    }

    onClose();

    // modal={false} skips Radix focus restoration, and the toggle is not a
    // Dialog.Trigger, so focus is returned explicitly.
    document.getElementById(`${id}-toggle`)?.focus();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange} modal={false}>
      <Dialog.Portal container={container ?? undefined} forceMount>
        <Dialog.Content
          forceMount
          inert={isOpen ? undefined : ''}
          className={classNames(styles.drawer, 'mobile-nav-dialog', {
            [styles.withAnnouncement]: hasAnnouncement,
          })}
          aria-label="Primary navigation"
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => {
            // The toggle is not a Dialog.Trigger, so focus is restored explicitly.
            event.preventDefault();
            document.getElementById(`${id}-toggle`)?.focus();
          }}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
        >
          <Dialog.Title className={styles.srOnly}>Primary navigation</Dialog.Title>
          <MobileMenu id={id} items={items} onNavigate={onClose} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

type MobileMenuProps = {
  id: string;
  items: HeaderMenuItem[];
  onNavigate: () => void;
};

function MobileMenu({ id, items, onNavigate }: MobileMenuProps) {
  return (
    <nav id={id} role="navigation" aria-label="Primary">
      <MobileMenuAccordion items={items} onNavigate={onNavigate} />
    </nav>
  );
}
